import i18n from "@/bootstrap/translations";
import { AuthenticationFormHeader } from "@/components/forms/AuthenticationFormHeader";
import { FormInput } from "@/components/forms/FormInput";
import { FormSubmitButton } from "@/components/forms/FormSubmitButton";
import { FormWrapper } from "@/components/forms/FormWrapper";
import { Card, CardContent } from "@/components/ui/card";
import { Routes } from "@/config/routes";
import { validation } from "@/domain/validation";
import { auth, ValidateOnboardTokenResponse } from "@/endpoints/dryp/auth";
import { useFormHandler } from "@/hooks/useFormHandler";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";

interface OnboardFormProps {
	onboardData: ValidateOnboardTokenResponse;
	token: string;
}

const validationFormSchema = z
	.object({
		firstName: z.string(),
		lastName: z.string().optional(),
		email: validation.email,
		password: validation.passwordWithRules,
		confirmPassword: validation.passwordWithRules,
	})
	.refine(({ password, confirmPassword }) => password === confirmPassword, {
		message: i18n.t("shared.forms.password.password_match"),
		path: ["confirmPassword"],
	});

type OnboardFormValues = z.infer<typeof validationFormSchema>;

export const OnboardForm: React.FC<OnboardFormProps> = observer(({ onboardData: { user, organization }, token }) => {
	const onboardApi = useAsyncStateStore(auth.onboard);
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { form, handleFormErrors } = useFormHandler(validationFormSchema, {
		defaultValues: {
			firstName: user?.firstName || "",
			lastName: user?.lastName || "",
			email: user?.email || "",
			password: "",
			confirmPassword: "",
		},
	});

	const onSubmit = async (data: OnboardFormValues) => {
		const { firstName, lastName, password } = data;
		try {
			await onboardApi.fetch({
				firstName,
				lastName: lastName || "",
				password,
				token,
			});

			// TODO Confirm login

			navigate(Routes.Dashboard.Home.path);
		} catch (error) {
			handleFormErrors(error);
		}
	};

	const organizationName = organization.Name;

	return (
		<Card>
			<AuthenticationFormHeader
				title={t("pages.onboard.form.title")}
				description={t("pages.onboard.form.description", { organizationName })}
			/>
			<CardContent>
				<FormWrapper form={form} onSubmit={onSubmit} error={onboardApi.error}>
					<div className="flex gap-1">
						<FormInput
							required
							name="firstName"
							type="text"
							label={t("shared.forms.first_name.label")}
							placeholder={t("shared.forms.first_name.placeholder")}
						/>
						<FormInput
							name="lastName"
							type="text"
							label={t("shared.forms.last_name.label")}
							placeholder={t("shared.forms.last_name.placeholder")}
						/>
					</div>
					<FormInput
						required
						disabled
						name="email"
						type="email"
						label={t("shared.forms.email.label")}
						placeholder={t("shared.forms.email.placeholder")}
					/>
					<FormInput
						required
						name="password"
						type="password"
						label={t("shared.forms.password.label")}
						placeholder={t("shared.forms.password.placeholder")}
					/>
					<FormInput
						required
						name="confirmPassword"
						type="password"
						label={t("pages.reset_password.repet_password_label")}
						placeholder={t("shared.forms.password.placeholder")}
					/>

					<FormSubmitButton
						isLoading={onboardApi.loading}
						buttonText={t("pages.onboard.form.button_text", { organizationName })}
						buttonLoadingText={t("pages.onboard.form.button_text_loading")}
					/>
				</FormWrapper>
			</CardContent>
		</Card>
	);
});
